import * as React from "react";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { classNames } from "react-extras";

const COLLAPSE_TIMING = 500; // ms

interface PanelBodyDivProps {
  collapsing: boolean;
  collapsed: boolean;
  open: boolean;
  opening: boolean;
  collapsible: boolean;
  maxHeight?: string;
}

const PanelBodyDiv = styled.div<PanelBodyDivProps>`
  max-height: ${props =>
    props.collapsible && (props.collapsed || props.collapsing)
      ? 0
      : props.maxHeight};
  padding: 0;
  overflow: ${props => (props.open ? "" : "hidden")};
  //overflow-y: ${props => (props.open ? "scroll" : "")};
  transition: max-height ${COLLAPSE_TIMING}ms;
`;

interface IProps {
  heading?: React.ReactNode;
  type?: BootstrapContextColor;
  footer?: React.ReactNode;
  children: React.ReactNode;
  collapsible?: boolean;
  startCollapsed?: boolean;
  maxBodyHeight?: string;
  headerItemRight?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

function Panel({
  heading,
  footer,
  type = "default",
  children,
  collapsible,
  startCollapsed,
  maxBodyHeight,
  headerItemRight,
  style,
  className,
}: IProps) {
  const [collapseState, setCollapseState] = useState<
    "collapsed" | "collapsing" | "open" | "opening"
  >(startCollapsed ? "collapsed" : "open");

  const collapsed = collapseState === "collapsed";
  const collapsing = collapseState === "collapsing";
  const open = collapseState === "open";
  const opening = collapseState === "opening";

  function toggleCollapse() {
    if (collapsed) {
      setCollapseState("opening");
      setTimeout(() => setCollapseState("open"), COLLAPSE_TIMING);
    } else if (open) {
      setTimeout(() => setCollapseState("collapsing"));
      setTimeout(() => setCollapseState("collapsed"), COLLAPSE_TIMING);
    }
  }

  // just for dev checks
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      if (collapsible && !maxBodyHeight) {
        // eslint-disable-next-line no-console
        console.warn(
          `You are using a collapsible Panel component without the 'maxBodyHeight' prop.
          This can cause strange behavior with the collapse animation.
          Recommend setting the 'maxBodyHeight' prop to be over but as close as possible to the actual
          max height of the content. Use math if you have to.`
            .split("\n")
            .map(s => s.trim())
            .join(" ")
        );
      }
    }
  }, [collapsible, maxBodyHeight]);

  return (
    <div
      className={classNames("panel", `panel-${type}`, className)}
      style={style}
    >
      {heading && (
        <div
          className={"panel-heading"}
          onClick={collapsible ? toggleCollapse : undefined}
          style={{ cursor: collapsible ? "pointer" : "" }}
        >
          {collapsible ? (
            <div className={"level"}>
              <div className="level-left">
                <div className="level-item">{heading}</div>
              </div>
              <div className="level-right">
                {headerItemRight && (
                  <div className={"level-item"}>{headerItemRight}</div>
                )}
                <div className="level-item">
                  <CollapseIcon collapsed={collapsed} collapsing={collapsing} />
                </div>
              </div>
            </div>
          ) : (
            heading
          )}
        </div>
      )}
      <PanelBodyDiv
        collapsible={collapsible}
        collapsing={collapsing}
        collapsed={collapsed}
        open={open}
        opening={opening}
        className="panel-body"
        maxHeight={collapsible ? maxBodyHeight || "2000px" : undefined}
      >
        {/*
          this div is for moving the padding from .panel-body to a nested div
          which fixes padding on the collapse animation
         */}
        <div style={{ padding: "15px" }}>{children}</div>
      </PanelBodyDiv>
      {footer && <div className={"panel-footer"}>{footer}</div>}
    </div>
  );
}

export default Panel;

function CollapseIcon({ collapsed, collapsing }) {
  return (
    <i
      className="fa fa-chevron-left"
      style={{
        transform: collapsed || collapsing ? "" : "rotate(-90deg)",
        transition: `transform 100ms`,
      }}
    />
  );
}



// WEBPACK FOOTER //
// ./app/javascript/components/core/Panel.tsx